
import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RichTextEditor from "../RichTextEditor";

interface ContentTabSectionProps {
  title: string;
  setTitle: (value: string) => void;
  leadText: string;
  setLeadText: (value: string) => void;
  content: string;
  setContent: (value: string) => void;
  uploadArticleMedia: (file: File) => Promise<string | null>;
  formErrors: Record<string, string>;
  articleId?: string;
}

const ContentTabSection = ({
  title,
  setTitle,
  leadText,
  setLeadText,
  content,
  setContent,
  uploadArticleMedia,
  formErrors,
  articleId
}: ContentTabSectionProps) => {
  // Function to handle image upload with additional metadata
  const handleImageUpload = async (file: File) => {
    console.log("Content tab initiating image upload. Article ID:", articleId || "none (new article)");
    try {
      // We're handling article media specially here
      const url = await uploadArticleMedia(file);
      console.log("Image upload completed with URL:", url);
      
      if (!url) {
        throw new Error("Failed to upload image");
      }
      
      return url;
    } catch (error) {
      console.error("Error in handleImageUpload:", error);
      throw error; // Re-throw to allow proper error handling in the editor
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input 
          id="title" 
          value={title} 
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Article title"
          required
          className={formErrors.title ? "border-red-500" : ""}
        />
        {formErrors.title && (
          <p className="text-red-500 text-sm">{formErrors.title}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="leadText">Lead Text / Summary</Label>
        <Textarea 
          id="leadText" 
          value={leadText} 
          onChange={(e) => setLeadText(e.target.value)}
          placeholder="A brief summary of the article"
          rows={3}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="content">Content</Label>
        <div className={formErrors.content ? "border border-red-500 rounded-md" : ""}>
          <RichTextEditor 
            content={content} 
            onChange={setContent}
            uploadImage={handleImageUpload}
            placeholder="Write your article content here..."
          />
        </div>
        {formErrors.content && (
          <p className="text-red-500 text-sm">{formErrors.content}</p>
        )}
      </div>
    </div>
  );
};

export default ContentTabSection;
